// src/pages/admin/CardInstallments.tsx
import { useEffect, useState } from "react";
import { authFetch } from "../../lib/auth";

// 서버에서 내려오는 타입
type AdminInstallment = {
  id: number;
  key: string; // 'bc', 'kb' ...
  name: string; // '비씨카드'
  term: string; // "2~5" (편집용)
  enabled: boolean;
  sort_order: number;
  updated_at: string;

  // ✅ 부분 무이자(새로 추가)
  partial_text?: string | null; // 예: "7개월(고객부담:1~3회차)\n9개월(고객부담:1~4회차)"
  partial_note?: string | null; // 예: "신한BC 제외"
};

// 카드사 로고
import logoBC from "../../assets/card-logos/bc.png";
import logoCiti from "../../assets/card-logos/citi.png";
import logoHana from "../../assets/card-logos/hana.png";
import logoHyundai from "../../assets/card-logos/hyundai.png";
import logoJeju from "../../assets/card-logos/jeju.png";
import logoJeonbuk from "../../assets/card-logos/jeonbuk.png";
import logoKakaoBank from "../../assets/card-logos/kakaobank.png";
import logoKB from "../../assets/card-logos/kb.png";
import logoKBank from "../../assets/card-logos/kbank.png";
import logoKwangju from "../../assets/card-logos/kwangju.png";
import logoLotte from "../../assets/card-logos/lotte.png";
import logoMG from "../../assets/card-logos/mg.png";
import logoNonghyup from "../../assets/card-logos/nonghyup.png";
import logoPost from "../../assets/card-logos/post.png";
import logoSamsung from "../../assets/card-logos/samsung.png";
import logoSave from "../../assets/card-logos/save.png";
import logoSinhan from "../../assets/card-logos/sinhan.png";
import logoSinhyup from "../../assets/card-logos/sinhyup.png";
import logoSoohyup from "../../assets/card-logos/soohyup.png";
import logoWoori from "../../assets/card-logos/woori.png";

const CARD_LOGOS: Record<string, string> = {
  bc: logoBC,
  kb: logoKB,
  sinhan: logoSinhan,
  samsung: logoSamsung,
  lotte: logoLotte,
  woori: logoWoori,
  nonghyup: logoNonghyup,
  jeonbuk: logoJeonbuk,
  kwangju: logoKwangju,
  hyundai: logoHyundai,
  citi: logoCiti,
  hana: logoHana,
  jeju: logoJeju,
  kakaobank: logoKakaoBank,
  kbank: logoKBank,
  mg: logoMG,
  post: logoPost,
  save: logoSave,
  sinhyup: logoSinhyup,
  soohyup: logoSoohyup,
};

// 서버 term("2~6개월") -> 편집용("2~6")
function stripGaewol(raw: string): string {
  if (!raw) return "";
  return raw.replace(/개월\s*$/u, "");
}

// 저장 시에만 "개월" 붙임
function normalizeTermForSave(raw: string): string {
  let v = raw.trim();
  if (!v) return v;
  if (v.includes("불가")) return v;
  if (v.includes("개월")) return v;
  return v + "개월";
}

export default function CardInstallments() {
  const [items, setItems] = useState<AdminInstallment[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const res = await authFetch("/api/admin/installments");
      if (!res.ok) throw new Error("load failed");
      const data = await res.json();

      if (data?.ok && Array.isArray(data.items)) {
        const mapped: AdminInstallment[] = data.items.map((it: any) => ({
          ...it,
          term: stripGaewol(it.term || ""),
          partial_text: it.partial_text ?? "",
          partial_note: it.partial_note ?? "",
        }));
        setItems(mapped);
      } else {
        throw new Error("invalid response");
      }
    } catch (e) {
      console.error(e);
      setError("목록을 불러오는 중 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateField = (id: number, field: keyof AdminInstallment, value: any) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, [field]: value } : it))
    );
  };

  const handleSave = async (item: AdminInstallment) => {
    setSavingId(item.id);
    setError(null);

    const termToSave = normalizeTermForSave(item.term);

    try {
      const res = await authFetch(`/api/admin/installments/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          term: termToSave,
          enabled: item.enabled,
          sort_order: item.sort_order,

          // ✅ 부분 무이자(저장)
          partial_text: (item.partial_text ?? "").toString(),
          partial_note: (item.partial_note ?? "").toString(),
        }),
      });

      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || "save failed");
      }

      const updatedFromServer = data.item || {};
      const nextTerm = stripGaewol(updatedFromServer.term ?? termToSave);

      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id
            ? {
                ...it,
                ...item,
                term: nextTerm,
                enabled: updatedFromServer.enabled ?? item.enabled,
                sort_order: updatedFromServer.sort_order ?? item.sort_order,
                partial_text: updatedFromServer.partial_text ?? item.partial_text ?? "",
                partial_note: updatedFromServer.partial_note ?? item.partial_note ?? "",
              }
            : it
        )
      );
    } catch (e) {
      console.error(e);
      setError("저장 중 오류가 발생했습니다.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="w-full max-w-5xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">카드사 무이자/할부 설정</h1>
        <p className="mt-1 text-sm text-slate-600">
          여기서 각 카드사의 무이자 구간(예: 2~5개월)과 부분 무이자 안내를 수정하면,
          홈 화면 혜택 영역에 반영됩니다.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          * 부분 무이자 “내용”은 여러 줄 입력을 권장합니다. (예: 7개월(고객부담:1~3회차) ↵ 9개월(고객부담:1~4회차))
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {/* ✅ 모바일: 카드 리스트 */}
      <div className="space-y-3 md:hidden">
        {!items.length ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            데이터가 없습니다.
          </div>
        ) : (
          items.map((item) => {
            const logo = CARD_LOGOS[item.key];
            return (
              <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {logo && (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50">
                        <img src={logo} alt={item.name} className="h-7 w-7 object-contain" />
                      </div>
                    )}
                    <div className="font-semibold text-slate-900">{item.name}</div>
                  </div>

                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={(e) => updateField(item.id, "enabled", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    노출
                  </label>
                </div>

                <div className="mt-3 space-y-3">
                  <div>
                    <div className="text-xs font-semibold text-slate-600">무이자 구간(표시값)</div>
                    <input
                      type="text"
                      value={item.term}
                      onChange={(e) => updateField(item.id, "term", e.target.value)}
                      className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                      placeholder="예) 2~5 / 할부 불가"
                    />
                    <div className="mt-1 text-[11px] text-slate-400">
                      숫자만 입력하면 자동으로 “개월”이 붙어 저장됩니다.
                    </div>
                  </div>

                  {/* ✅ 부분 무이자 내용 */}
                  <div>
                    <div className="text-xs font-semibold text-slate-600">부분 무이자 내용(선택)</div>
                    <textarea
                      value={(item.partial_text ?? "") as any}
                      onChange={(e) => updateField(item.id, "partial_text", e.target.value)}
                      className="mt-1 min-h-[84px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                      placeholder={"예)\n7개월(고객부담:1~3회차)"}
                    />
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-slate-600">부분 무이자 비고/제외(선택)</div>
                    <input
                      type="text"
                      value={(item.partial_note ?? "") as any}
                      onChange={(e) => updateField(item.id, "partial_note", e.target.value)}
                      className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-slate-400"
                      placeholder="예) 신한BC 제외 / 기업·개인사업자 카드 제외"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-semibold text-slate-600">정렬</div>
                      <input
                        type="number"
                        value={item.sort_order}
                        onChange={(e) =>
                          updateField(item.id, "sort_order", Number(e.target.value) || 0)
                        }
                        className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-right text-sm outline-none focus:border-slate-400"
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => handleSave(item)}
                        disabled={savingId === item.id}
                        className="h-11 w-full rounded-xl bg-brand-500 px-4 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
                      >
                        {savingId === item.id ? "저장 중..." : "저장"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ✅ 데스크탑: 테이블 */}
      <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs font-semibold text-slate-500">
              <th className="px-4 py-2">카드사</th>
              <th className="w-40 px-4 py-2">무이자 구간</th>
              <th className="px-4 py-2">부분 무이자 내용</th>
              <th className="w-56 px-4 py-2">부분 무이자 비고</th>
              <th className="w-20 px-4 py-2">노출</th>
              <th className="w-20 px-4 py-2">정렬</th>
              <th className="w-24 px-4 py-2 text-right">작업</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const logo = CARD_LOGOS[item.key];
              return (
                <tr key={item.id} className="border-t border-slate-100 align-top">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {logo && (
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-50">
                          <img src={logo} alt={item.name} className="h-7 w-7 object-contain" />
                        </div>
                      )}
                      <div className="text-sm font-medium text-slate-900">{item.name}</div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={item.term}
                      onChange={(e) => updateField(item.id, "term", e.target.value)}
                      className="w-28 rounded-md border border-slate-200 px-2 py-1 text-sm"
                      placeholder="예) 2~5"
                    />
                    <div className="mt-1 text-[10px] text-slate-400">
                      숫자만 입력하면 “개월”이 붙어 저장됩니다.
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <textarea
                      value={(item.partial_text ?? "") as any}
                      onChange={(e) => updateField(item.id, "partial_text", e.target.value)}
                      className="min-h-[76px] w-full rounded-md border border-slate-200 px-2 py-1 text-sm"
                      placeholder={"예)\n7개월(고객부담:1~3회차)"}
                    />
                  </td>

                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={(item.partial_note ?? "") as any}
                      onChange={(e) => updateField(item.id, "partial_note", e.target.value)}
                      className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm"
                      placeholder="예) 신한BC 제외"
                    />
                  </td>

                  <td className="px-4 py-3">
                    <label className="inline-flex items-center gap-1 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={(e) => updateField(item.id, "enabled", e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      노출
                    </label>
                  </td>

                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={item.sort_order}
                      onChange={(e) =>
                        updateField(item.id, "sort_order", Number(e.target.value) || 0)
                      }
                      className="w-16 rounded-md border border-slate-200 px-2 py-1 text-xs"
                    />
                  </td>

                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleSave(item)}
                      disabled={savingId === item.id}
                      className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-400 disabled:opacity-60"
                    >
                      {savingId === item.id ? "저장 중..." : "저장"}
                    </button>
                  </td>
                </tr>
              );
            })}

            {!items.length && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-xs text-slate-500">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        * “정렬” 값이 작을수록 홈 화면 상단(왼쪽)에 먼저 노출됩니다.
      </p>
    </div>
  );
}
